import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { INumberFieldSpecs } from './INumberFieldSpecs';
import { NumberInputComponent } from './number-input.component';
import { IconDefinition, faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { ELabelPosition } from "libraries/module/formbuilder/fields/form-field/form-field.component";

@Component({
    selector: 'number',
    templateUrl: './number.component.html',
    styleUrls: ['./number.component.scss']
})
export class NumberComponent implements OnChanges, OnInit {
    @Input()
    public fieldConfig: INumberFieldSpecs;
    @Input()
    public disabled: boolean = false;
    @Input()
    public disabledStep: boolean = false;
    @Input()
    public forceFocus: boolean = false;
    @Input()
    public forceBlur: boolean = false;
    @Input()
    public showPlusAndMinus: boolean = true;
    @Input()
    public isFull: boolean = false;
    @Input()
    public isFullField: boolean = false;
    @Input()
    public isAlignBottom: boolean = false;
    @Input()
    public emitKeys: string[] | undefined | null = ["Enter", "Esc", "Escape", "ArrowUp", "Up", "ArrowRight", "Right", "ArrowDown", "Down", "ArrowLeft", "Left"];
    @Input()
    public patchOnChange: boolean = true;
    @Input()
    public align: "center" | "left" | "right";
    @Input()
    public labelPosition: ELabelPosition = ELabelPosition.top;

    @Output()
    public readonly onBlur: EventEmitter<number> = new EventEmitter();
    @Output()
    public readonly onSelect: EventEmitter<number> = new EventEmitter();
    @Output()
    public readonly onFocus: EventEmitter<number> = new EventEmitter();
    @Output()
    public readonly onChange: EventEmitter<number> = new EventEmitter();
    @Output()
    public readonly onKeyUp: EventEmitter<string> = new EventEmitter<string>();
    @Output()
    public readonly onEnter: EventEmitter<null> = new EventEmitter<null>();
    @Output()
    public readonly onClick: EventEmitter<null> = new EventEmitter<null>();
    @Output()
    public readonly onUpDownClick: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('inputField')
    public inputField: NumberInputComponent;

    public value: number;

    public iconPlus: IconDefinition = faPlus;
    public iconMinus: IconDefinition = faMinus;

    public ngOnInit(): void {
        if (!this.align) {
            this.align = this.showPlusAndMinus ? "center" : "right";
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.showPlusAndMinus && !this.align) {
            this.align = this.showPlusAndMinus ? "center" : "right";
        }

        if (changes.forceFocus && changes.forceFocus.currentValue) {
            if (this.inputField) {
                this.inputField.focus();
            }
        }

        if (changes.forceBlur && changes.forceBlur.currentValue) {
            if (this.inputField) {
                this.inputField.blur();
            }
        }

        if (changes.disable) {
            if (this.disabled) {
                this.fieldConfig.formGroup.disable({emitEvent: false});
                this.value = this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).value;
            } else {
                this.fieldConfig.formGroup.enable({emitEvent: false});
            }
        }
    }

    public allowIncrease(): boolean {
        if (!this.showPlusAndMinus) {
            return false;
        }
        if (!hasLimit(this.fieldConfig.max)) {
            return true;
        }
        const fieldValue: number = this.fieldConfig.formGroup.value[this.fieldConfig.formControlName];
        const step: number = this.fieldConfig.step || 1;
        return Number(fieldValue + step) <= Number(this.fieldConfig.max);
    }

    public allowDecrease(): boolean {
        if (!this.showPlusAndMinus) {
            return false;
        }
        if (!hasLimit(this.fieldConfig.min)) {
            return true;
        }
        const fieldValue: number = this.fieldConfig.formGroup.value[this.fieldConfig.formControlName];
        const step: number = this.fieldConfig.step || 1;

        return Number(fieldValue - step) >= Number(this.fieldConfig.min);
    }

    public increaseOnClick(): void {
        if (!this.disabledStep) {
            if (this.allowIncrease() && !this.disabled) {
                this.increase();
            }
        }
    }

    public decreaseOnClick(): void {
        if (!this.disabledStep) {
            if (this.allowDecrease() && !this.disabled) {
                this.decrease();
            }
        }
    }

    public onKeyup(keyEvent: KeyboardEvent): void {
        if (!this.disabledStep && this.showPlusAndMinus) {
            switch (keyEvent.which) {
                case (38)://UP
                    keyEvent.preventDefault();
                    if (this.allowIncrease() && !this.disabled) {
                        this.increase();
                    }
                    break;

                case (40): //DOWN
                    keyEvent.preventDefault();
                    if (this.allowDecrease() && !this.disabled) {
                        this.decrease();
                    }
                    break;

                case (13): // ENTER
                    this.onEnter.emit();
                    break;

                default:
                    break;

            }
        } else {
            if (!this.emitKeys || this.emitKeys.includes(keyEvent.key)) {
                keyEvent.preventDefault();
                this.onKeyUp.emit(keyEvent.key);
            }
        }
    }

    public increase(): void {
        const currentValue: number = this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).value;
        const step: number = this.fieldConfig.step || 1;

        this.fieldConfig.formGroup.get(this.fieldConfig.formControlName)
            .patchValue(checkValue((currentValue ? Number(currentValue) : 0) + step, this.fieldConfig.min, this.fieldConfig.max));

        this.onUpDownClick.emit(this.getCurrentValue());
    }

    public decrease(): void {
        const currentValue: number = this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).value;
        const step: number = this.fieldConfig.step || 1;

        this.fieldConfig.formGroup.get(this.fieldConfig.formControlName)
            .patchValue(checkValue((currentValue ? Number(currentValue) : 0) - step, this.fieldConfig.min, this.fieldConfig.max));

        this.onUpDownClick.emit(this.getCurrentValue());
    }

    public getCurrentValue(): number {
        return this.inputField ?
            this.inputField.getCurrentValue() :
            this.fieldConfig.formGroup.get(this.fieldConfig.formControlName).value;
    }
}

function hasLimit(num: number): boolean {
    if (num === 0) {
        return true;
    }
    return Boolean(num);
}

function checkValue(candidate: number, min: number, max: number): number {
    let result: number = candidate;

    if (min !== undefined && min !== null) {
        result = Math.max(min, result);
    }

    if (max !== undefined && max !== null) {
        result = Math.min(max, result);
    }

    return result;
}
